<template>
  <v-container v-if="clinicInfo" fluid class="white px-0 elevation-4">
    <main-app-bar :title="title">
      <template v-slot:buttons>
        <v-btn v-if="$router.currentRoute.name === 'editClinic'" class="mr-4" color="red" dark @click="deleteBtnHandler">Delete</v-btn>
        <v-btn class="mx-4" :to="{name: 'clinics'}">Cancel</v-btn>
        <v-btn color="primary" @click.once="submitMainForm">Save</v-btn>
      </template>
    </main-app-bar>
    <v-form ref="mainForm" v-if="!clinicErrorMessage">
        <v-flex class="d-block grey lighten-4" style="margin-top: -12px" v-if="clinicId">
            <div class="pa-3">
                <v-btn-toggle
                    v-model="activeTab"
                    color="primary"
                    tile
                    group
                    mandatory
                >
                    <v-btn class="ma-0 mr-3 px-5 py-4" value="opening">
                        Opening
                    </v-btn>
                    <v-btn class="ma-0 mr-3 px-5 py-4" value="settings">
                        Settings
                    </v-btn>
                </v-btn-toggle>
            </div>
        </v-flex>
      <v-container class="px-5" v-if="activeTab === 'settings'">
        <v-layout row px-4>
          <v-flex xs3 class="d-flex align-center">
            <h3 class="ma-0">Title*</h3>
          </v-flex>
          <v-flex xs6 class="align-center">
            <v-text-field
              v-model="clinicInfo.title"
              placeholder="Clinic Title"
              value="true"
              required="false"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout row px-4>
          <v-flex xs3 class="d-flex align-center">
            <h3 class="ma-0">Xero name</h3>
          </v-flex>
          <v-flex xs6 class="align-center">
            <v-text-field
              v-model="clinicInfo.accountingTitle"
              placeholder="Xero organization name"
              value="true"
              required="false"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout row px-4>
          <v-flex xs3 class="d-flex align-center">
            <h3 class="ma-0">Marketing dashbord</h3>
          </v-flex>
          <v-flex xs6 class="align-center">
            <v-text-field
              v-model="clinicInfo.marketingDashboardUrl"
              placeholder="Marketing dashboard link"
              value="true"
              required="false"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout row px-4>
          <v-flex xs3 class="d-flex align-center">
            <h3 class="ma-0">Group*</h3>
          </v-flex>
          <v-flex xs6 class="align-center">
            <v-autocomplete
              :items="getGroups"
              v-model="clinicInfo.groupId"
              item-text="title"
              :rules="groupTitleRules"
              item-value="id"
              placeholder="Select Group"
              :disabled="!!clinicInfo.groupId && !!clinicId"
            ></v-autocomplete>
          </v-flex>
        </v-layout>
        <v-layout row px-4>
          <v-flex xs3 class="d-flex">
            <h3 class="ma-0 pt-5">Sub-unit*</h3>
          </v-flex>
          <v-flex xs6 class="align-center">
            <v-flex xs12 class="d-flex align-center">
              <v-form ref="addSurgeryForm" lazy-validation>
                <v-flex xs12 class="d-flex align-center">
                  <v-text-field
                    v-model="surgeryTitle"
                    placeholder="Sub-unit Title"
                    :rules="surgeryTitleRules"
                    value="true"
                    required="false"
                  ></v-text-field>
                  <v-btn color="primary" @click="addSurgeryHandler" class="ml-5">Add</v-btn>
                </v-flex>
              </v-form>
            </v-flex>
            <v-flex>
              <v-list-item
                v-for="item in clinicInfo.surgeries"
                :key="item.id || item.tempId"
              >
                <v-list-item-content>
                  <v-list-item-title v-text="item.value"></v-list-item-title>
                </v-list-item-content>
                <v-list-item-content>
                  <v-list-item-title>
                    <v-btn fab small primary="transparent" @click="removeSurgery(item.id || item.tempId)" class="elevation-0">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-flex>
          </v-flex>
        </v-layout>
      </v-container>
        <v-container class="px-5" v-show="activeTab === 'opening'">
            <today-opening-days ref="todayOpeningDays" v-if="clinicId" :clinicId="clinicId" :surgeries="clinicInfo.surgeries"></today-opening-days>
            <default-opening-days ref="defaultOpeningDays" v-if="clinicId" :clinicId="clinicId" :surgeries="clinicInfo.surgeries"></default-opening-days>
            <opening-days-override ref="openingDaysOverride" v-if="clinicId" :clinicId="clinicId" :surgeries="clinicInfo.surgeries"></opening-days-override>
        </v-container>
    </v-form>
    <div v-else>
      <h2 class="text-center">{{clinicErrorMessage}}</h2>
    </div>
  </v-container>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import validationRules from '@/common/validationRules'
  import MainAppBar from '@/components/MainAppBar'
  import TodayOpeningDays from './components/TodayOpeningDays'
  import DefaultOpeningDays from './components/DefaultOpeningDays'
  import OpeningDaysOverride from './components/OpeningDaysOverride'
  import locale from '@/locale/index'

  export default {
    data: () => ({
      activeTab: 'opening', // settings
      clinicId: null,
      clinicInfo: {
        title: '',
        surgeries: [],
        groupId: null,
        accountingTitle: '',
        marketingDashboardUrl: ''
      },
      title: '',
      surgeryTitle: '',
      clinicErrorMessage: '',
      surgeryTitleRules: [
        v => !!v || 'Title is required',
        v => (v && (v.length > 0 && v.length <= 100)) || 'Title must be between 1 and 100 characters'
      ],
      groupTitleRules: validationRules.select
    }),
    computed: {
      ...mapGetters(['getClinicById', 'getGroups'])
    },
    methods: {
      ...mapActions(['fetchClinicInfo', 'editClinicInfo', 'addClinic', 'showAlert', 'deleteClinic']),
      addSurgeryHandler () {
        if (this.$refs.addSurgeryForm.validate()) {
          const surgery = {
            id: null,
            tempId: this.$uuid.v1(),
            value: this.surgeryTitle
          }
          this.clinicInfo.surgeries.push(surgery)
          this.surgeryTitle = ''
          this.$refs.addSurgeryForm.resetValidation()
        }
      },
      removeSurgery (id) {
        this.clinicInfo.surgeries = this.clinicInfo.surgeries.filter(surgery => {
          if (surgery.tempId) {
            return surgery.tempId !== id
          } else {
            return surgery.id !== id
          }
        })
        this.$refs.defaultOpeningDays.createOpeningDaysData()
        this.$refs.openingDaysOverride.createOverrideData(null, this.clinicInfo.surgeries)
      },
      async submitMainForm () {
        if (this.$refs.mainForm.validate()) {
          if (this.clinicInfo.surgeries.length < 1) {
            this.showAlert({
              text: 'You must add at least 1 Sub-unit',
              type: 'error'
            })
            return
          }
          this.clinicInfo.surgeries.forEach(item => {
            if (item.additional) {
              delete item.additional
            }
          })
          if (this.clinicId) {
            const response = await this.$refs.defaultOpeningDays.saveOpeningDays()
            const overrideResponse = await this.$refs.openingDaysOverride.saveOverrideData()
            const todayOverrideResponse = await this.$refs.todayOpeningDays.saveOverrideData()
            if (!response) {
              return false
            }
            if (!overrideResponse || !todayOverrideResponse) {
              this.$refs.defaultOpeningDays.createOpeningDaysData()
              return false
            }
            this.editClinicInfo(this.clinicInfo)
          } else {
            this.addClinic(this.clinicInfo)
          }
        }
      },
      async deleteBtnHandler () {
        if (await this.$root.$confirmDialog.open(this.clinicInfo.title)) {
          this.deleteClinic(this.clinicInfo.id)
        }
      }
    },
    async mounted () {
      const clinicId = +this.$router.currentRoute.params.id
      this.clinicId = +this.$router.currentRoute.params.id
      this.clinicErrorMessage = ''
      if (clinicId) {
        this.fetchClinicInfo(clinicId).then(async () => {
          this.clinicInfo = this.getClinicById(clinicId)
          this.clinicInfo.surgeries.sort(function (a, b) {
            if (a.id > b.id) {
              return -1
            }
          })
          this.title = this.clinicInfo.title
          await this.$refs.defaultOpeningDays.createOpeningDaysData()
          await this.$refs.openingDaysOverride.fetchOverrideData()
          await this.$refs.todayOpeningDays.fetchOverrideData()
        }, e => {
          this.clinicErrorMessage = locale.serverErrorText
        })
      } else {
        this.title = 'Add clinic'
        this.activeTab = 'settings'
      }
    },
    components: {
      MainAppBar,
      DefaultOpeningDays,
      OpeningDaysOverride,
      TodayOpeningDays
    }
  }
</script>

<style lang="scss">
    p.tooltip-fixed {
        max-width: 600px;
        width: 100%;
        margin-bottom: 0;
    }
  .bordered-table {
    width: 100%;
    max-width: 852px;
    .v-data-table__wrapper {
    }
    table {
      border-collapse: collapse;
      width: 99.5%;
      td {
        padding-left: 10px;
        padding-right: 10px;
        border: 1px solid rgba(0, 0, 0, 0.12);
      }
      thead {
        th {
          border-left: 1px solid #fff;
          padding-left: 10px;
          padding-right: 10px;
        }
        tr {
          th {
            height: 20px;
            padding-top: 0;
            padding-bottom: 0;
          }
          &:first-child {
            th {
              padding: 5px !important;
              height: 20px;
            }
          }
        }
      }
    }
  }
</style>
